import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, Sparkles, CheckCircle2, X } from 'lucide-react';
import { subscribeOta, type OtaProgress } from '@/lib/ota';
import { useTranslation } from 'react-i18next';

/**
 * OTA progress UI — passive, never blocks the app.
 *
 *   DOWNLOADING → "Baixando atualização" pill with progress %.
 *   READY       → brief "Atualizando… reiniciando o app" (set() reloads in <1s).
 *   APPLIED     → after the reload, a "Atualizado! · vX" success popup that
 *                 auto-dismisses (~7s). This is what confirms the new version.
 *
 * The whole flow is automatic: download → apply → auto-restart → confirm.
 */
export function OtaUpdaterOverlay() {
  const { t } = useTranslation();
  const [state, setState] = useState<OtaProgress>({ state: 'idle', percent: 0 });
  const [dismissed, setDismissed] = useState(false);
  const [appliedShownAt, setAppliedShownAt] = useState<number | null>(null);

  useEffect(() => {
    return subscribeOta((p) => {
      setState(p);
      // A new meaningful state deserves to reappear.
      if (p.state === 'ready' || p.state === 'downloading' || p.state === 'applied') {
        setDismissed(false);
        if (p.state === 'applied') setAppliedShownAt(Date.now());
      }
    });
  }, []);

  // Auto-dismiss the "Atualizado" popup after 7s.
  useEffect(() => {
    if (state.state !== 'applied' || !appliedShownAt) return;
    const t = window.setTimeout(() => setDismissed(true), 7000);
    return () => clearTimeout(t);
  }, [state.state, appliedShownAt]);

  const showDownload = !dismissed && state.state === 'downloading';
  const showReady = !dismissed && state.state === 'ready';
  const showApplied = !dismissed && state.state === 'applied';
  // Versions are now short semver ("0.0.412") — show them whole. The old
  // .slice(0, 7) existed to truncate long git shas; it would mangle semver.
  const ver = state.pendingVersion ?? '';

  const wrapCls =
    'fixed inset-x-0 bottom-[max(env(safe-area-inset-bottom),16px)] z-[10000] flex justify-center px-4 pointer-events-none';

  return (
    <AnimatePresence>
      {showDownload && (
        <motion.div
          key="dl"
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 60 }}
          transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
          className={wrapCls}
        >
          <div
            className="w-full max-w-sm rounded-2xl border bg-[#0A0B0F]/95 backdrop-blur-xl shadow-[0_20px_60px_-12px_rgba(0,102,255,0.3)] pointer-events-auto"
            style={{ borderColor: 'rgba(0,102,255,0.3)' }}
          >
            <div className="p-3.5 flex items-center gap-3">
              <div
                className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#0066FF] to-[#2DD4BF] shadow-md shrink-0"
                style={{ animation: 'bootPulse 1.4s ease-in-out infinite' }}
              >
                <Download className="h-4 w-4 text-white" strokeWidth={2.5} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold leading-tight text-white">
                  {t('componentsMisc:otaUpdater.downloading')}
                </div>
                <div className="mt-1 flex items-center gap-2">
                  <div className="flex-1 h-1 rounded-full bg-white/10 overflow-hidden">
                    <motion.div
                      className="h-full bg-gradient-to-r from-[#0066FF] to-[#2DD4BF]"
                      initial={{ width: 0 }}
                      animate={{ width: `${state.percent}%` }}
                      transition={{ duration: 0.3, ease: 'easeOut' }}
                    />
                  </div>
                  <span className="text-[10px] font-mono tabular-nums text-white/70 shrink-0 w-9 text-right">
                    {Math.round(state.percent)}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {showReady && (
        <motion.div
          key="ready"
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 60 }}
          transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
          className={wrapCls}
        >
          <div
            className="w-full max-w-sm rounded-2xl border bg-[#0A0B0F]/95 backdrop-blur-xl shadow-[0_20px_60px_-12px_rgba(0,102,255,0.35)] pointer-events-auto"
            style={{ borderColor: 'rgba(0,102,255,0.35)' }}
          >
            <div className="p-3.5 flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#0066FF] to-[#2DD4BF] shadow-md shrink-0">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                >
                  <Sparkles className="h-4 w-4 text-white" />
                </motion.div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold text-white">{t('componentsMisc:otaUpdater.applying')}</div>
                <div className="text-[11px] text-white/60 mt-0.5">
                  {t('componentsMisc:otaUpdater.restarting')}{ver ? ` · v${ver}` : ''}.
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {showApplied && (
        <motion.div
          key="applied"
          initial={{ opacity: 0, y: 60, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 60 }}
          transition={{ duration: 0.35, ease: [0.34, 1.56, 0.64, 1] }}
          className={wrapCls}
        >
          <div
            className="w-full max-w-sm rounded-2xl border bg-[#0A0B0F]/95 backdrop-blur-xl shadow-[0_20px_60px_-12px_rgba(45,212,191,0.45)] pointer-events-auto"
            style={{ borderColor: 'rgba(45,212,191,0.4)' }}
          >
            <div className="p-3.5 flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#2DD4BF] to-[#0066FF] shadow-md shrink-0">
                <CheckCircle2 className="h-4 w-4 text-white" strokeWidth={2.5} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold text-white">{t('componentsMisc:otaUpdater.applied')}</div>
                <div className="text-[11px] text-white/60 mt-0.5 font-mono tabular-nums">
                  {ver ? t('componentsMisc:otaUpdater.onVersion', { ver }) : t('componentsMisc:otaUpdater.onLatest')}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setDismissed(true)}
                className="text-white/40 hover:text-white/80 transition-colors p-1 -m-1 shrink-0"
                aria-label="OK"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Re-export to avoid breaking older imports.
export { Sparkles };
